import { buildApiUrl } from '@/app/lib/api-config';
import type { BlockingReason, IncidentStatus } from '@evimo/types';
import { OmpCriticalityBand } from '@evimo/types/enums/omp-criticality-band';
import type {
  AIDiagnostics,
  ConcernedLog,
  ConnectorSnapshot,
  DemoIncident,
  IncidentDetail,
  IncidentSeverity,
  TimelineEntry,
} from '@/app/dashboard/mock-data';
import { buildListIncidentsPath } from '~/app/services/incident/buildListIncidentsPath';
import { z } from 'zod';

const incidentStatusSchema = z.enum([
  'NEW',
  'RCA_IN_PROGRESS',
  'REMOTE_PROPOSED',
  'REMOTE_APPLIED',
  'FIELD_REQUIRED',
  'FIELD_IN_PROGRESS',
  'BLOCKED',
  'RESOLVED',
  'CLOSED',
]);

const resolutionTypeSchema = z.enum([
  'FIXED_REMOTE',
  'FIXED_FIELD',
  'AUTO_RESOLVED',
  'NO_FAULT_FOUND',
  'FALSE_POSITIVE',
  'DUPLICATE',
  'CANCELLED',
  'WONT_FIX',
]);

const blockingReasonSchema = z.enum([
  'WAITING_PARTS',
  'WAITING_CLIENT',
  'WAITING_DSO',
  'WAITING_CPO_BACKEND',
  'WAITING_SITE_ACCESS',
  'WAITING_SUPERVISOR_APPROVAL',
  'WAITING_TELEMETRY_WINDOW',
  'WAITING_VENDOR_RMA',
]);

const paginationMetaSchema = z.object({
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  totalPages: z.number().int(),
});

const incidentDashboardTimeRangeSchema = z.enum(['24h', '48h', '30d', 'all']);
const incidentDashboardChartRangeSchema = z.enum(['1m', '1y', 'all']);

const incidentDashboardStatsSchema = z.object({
  timeRange: incidentDashboardTimeRangeSchema,
  total: z.number().int().nonnegative(),
  open: z.number().int().nonnegative(),
  closed: z.number().int().nonnegative(),
  byStatus: z.record(z.string(), z.number().int().nonnegative()),
  kpis: z.object({
    timeRange: incidentDashboardTimeRangeSchema,
    resolvedCount: z.number().int().nonnegative(),
    firstTimeFixRate: z.number().int().nonnegative(),
    remoteResolutionRate: z.number().int().nonnegative(),
    meanTimeToRepairHours: z.number().nonnegative().nullable(),
    repairTimeSampleSize: z.number().int().nonnegative(),
    successfulStartRate: z.number().int().nonnegative().nullable(),
  }),
  evolution: z.object({
    chartRange: incidentDashboardChartRangeSchema,
    labels: z.array(z.string()),
    counts: z.array(z.number().int().nonnegative()),
  }),
});

const ompCriticalityBandSchema = z.enum(
  Object.values(OmpCriticalityBand) as [
    OmpCriticalityBand,
    ...OmpCriticalityBand[],
  ],
);

const ompAssetContextSchema = z.object({
  assetId: z.string(),
  assetName: z.string().nullable(),
  dailyRevenue: z.number(),
  currency: z.string(),
  landlordOrgId: z.string().nullable(),
  landlordName: z.string().nullable(),
  penaltyPerDayAmount: z.number(),
  penaltyCurrency: z.string(),
  openHours: z.number(),
  completedPenaltyDays: z.number(),
  slaPenaltyAccrued: z.number(),
});

const ompProposalSchema = z.object({
  oemPartnerId: z.string(),
  oemName: z.string(),
  interventionWithinHours: z.number(),
  interventionLabel: z.string(),
  oemCost: z.number(),
  oemCurrency: z.string(),
  revenueLoss: z.number(),
  slaPenalty: z.number(),
  totalImpact: z.number(),
  isRecommended: z.boolean(),
});

const incidentOmpSummarySchema = z.object({
  totalImpact: z.number(),
  currency: z.string(),
  criticalityBand: ompCriticalityBandSchema,
  recommendedLabel: z.string(),
  openHours: z.number(),
  completedPenaltyDays: z.number(),
  dailyRevenueTotal: z.number(),
  slaPenaltyAccrued: z.number(),
  revenueLoss: z.number(),
  oemCost: z.number(),
  missingData: z.array(z.string()).optional(),
  computedAt: z.string(),
  proposals: z.array(ompProposalSchema),
  assets: z.array(ompAssetContextSchema),
});

const apiIncidentSummarySchema = z.object({
  id: z.string(),
  identifier: z.string(),
  code: z.string(),
  title: z.string(),
  status: incidentStatusSchema,
  availableTransitions: z.array(incidentStatusSchema).optional(),
  resolutionType: resolutionTypeSchema.nullish(),
  blockingReason: blockingReasonSchema.nullish(),
  severity: z.number().nullable(),
  severityLevel: z.string().nullable(),
  priority: z.string().nullable(),
  description: z.string().nullable(),
  aiSummary: z.string().nullable(),
  origin: z.preprocess((value) => {
    if (Array.isArray(value)) {
      const items = value
        .filter((item): item is string => typeof item === 'string')
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
      return items.length > 0 ? items : ['AI diagnosis'];
    }
    if (typeof value === 'string' && value.trim()) {
      return [value.trim()];
    }
    return ['AI diagnosis'];
  }, z.array(z.string())),
  stationId: z.string().nullable(),
  stationName: z.string().nullable(),
  location: z.string().nullable(),
  affectedChargers: z.number().int().nonnegative(),
  totalChargers: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
  openedAt: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  closedAt: z.string().nullable(),
  primaryAsset: z
    .object({
      id: z.string(),
      name: z.string(),
      serialNumber: z.string(),
    })
    .nullable()
    .optional()
    .transform((value) => value ?? null),
  omp: incidentOmpSummarySchema.nullish(),
});

const apiIncidentListResponseSchema = z.object({
  data: z.array(apiIncidentSummarySchema),
  meta: paginationMetaSchema,
});

const apiTriggeredIncidentItemSchema = z.object({
  id: z.string(),
  identifier: z.string(),
  code: z.string(),
  title: z.string(),
  status: incidentStatusSchema,
  availableTransitions: z.array(incidentStatusSchema).optional(),
  severity: z.number().nullable(),
  severityLevel: z.string().nullable(),
  aiSummary: z.string().nullable(),
  stationId: z.string().nullable(),
  stationName: z.string().nullable(),
  location: z.string().nullable(),
  affectedChargers: z.number().int().nonnegative(),
  totalChargers: z.number().int().nonnegative(),
  createdAt: z.string(),
  openedAt: z.string().nullable(),
  primaryAsset: z
    .object({
      id: z.string(),
      name: z.string(),
      serialNumber: z.string(),
    })
    .nullable(),
});

const apiTriggeredIncidentListResponseSchema = z.object({
  data: z.array(apiTriggeredIncidentItemSchema),
  meta: paginationMetaSchema,
});

const apiTimelineEntrySchema = z.object({
  id: z.string(),
  occurredAt: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  source: z.string(),
  sourceLabel: z.string(),
  rawCode: z.string().nullable(),
  translatedCode: z.string().nullable(),
  normalizedState: z.string().nullish(),
  stateConflict: z.boolean().optional(),
});

const apiConnectorSchema = z.object({
  code: z.string(),
  name: z.string(),
  connectorType: z.string().nullable(),
  status: z.string().nullable(),
  note: z.string().nullable(),
});

const apiRequiredPartSchema = z.object({
  partNumber: z.string().nullable(),
  description: z.string(),
});

const apiRemoteActionSchema = z.object({
  action: z.string(),
  status: z.string(),
  timestamp: z.string(),
});

const apiTriggeredArmCommandSchema = z.object({
  commandId: z.string(),
  command: z.string().optional(),
  remediationPlanId: z.string().optional(),
  actionId: z.string().optional(),
  createdAt: z.string().optional(),
  targetAssetId: z.string().optional(),
  protocol: z.string().optional(),
  operation: z.string().optional(),
  executionTimeoutMs: z.number().optional(),
  isSuccess: z.boolean().optional(),
});

const apiTriggerRemoteActionResponseSchema = z.object({
  incidentId: z.string(),
  armRemediationCommands: z.object({
    commands: z.array(apiTriggeredArmCommandSchema),
  }),
});

const apiLaunchOnsiteInterventionResponseSchema = z.object({
  incidentId: z.string(),
  magicLink: z.string(),
  recommendedActions: z.array(z.string()),
  generatedAt: z.string(),
});

const diagnosisRecommendationActionSchema = z.object({
  actionDisplayId: z.string().optional(),
  actionDescription: z.string().optional(),
  remote: z.boolean().optional(),
  command: z.string().optional(),
});

const diagnosisSnapshotSchema = z.object({
  diagnosis: z
    .object({
      remediation: z
        .object({
          actions: z.array(diagnosisRecommendationActionSchema).optional(),
        })
        .optional(),
    })
    .optional(),
  armRemediationCommands: z
    .object({
      commands: z.array(apiTriggeredArmCommandSchema),
    })
    .optional(),
});

const apiDiagnosticsSchema = z.object({
  incidentSummary: z.string().nullable(),
  rootCauseAnalysis: z.string().nullable(),
  remoteActions: z.array(apiRemoteActionSchema),
  recommendedOnSiteActions: z.array(z.string()),
  estimatedResolutionTime: z.string().nullable(),
  partsRequired: z.array(apiRequiredPartSchema),
  latestDiagnosisSnapshot: z
    .record(z.string(), z.unknown())
    .nullable()
    .optional(),
  autoDiagnosisDueAt: z.string().nullable().optional(),
  rulesUsed: z
    .array(
      z.object({
        ruleId: z.string(),
        version: z.number(),
        source: z.enum(['GLOBAL', 'TENANT']),
        title: z.string().nullable(),
      }),
    )
    .optional(),
  rulesUnavailable: z.boolean().optional(),
});

const apiDiagnoseIncidentResponseSchema = z.object({
  status: z.enum(['COMPLETED', 'NO_DIAGNOSTIC_AVAILABLE']),
  incidentRequest: z.unknown(),
  diagnosis: z.unknown(),
  outputFilePath: z.string(),
});

const apiTransitionIncidentResponseSchema = z.object({
  incidentId: z.string(),
  identifier: z.string().optional(),
  status: incidentStatusSchema,
  blockingReason: blockingReasonSchema.nullish(),
  resolvedAt: z.string().nullable(),
  closedAt: z.string().nullable(),
  availableTransitions: z.array(incidentStatusSchema),
});

const apiConcernedLogSchema = z.object({
  logId: z.string(),
  receivedAt: z.string().nullable(),
  isTreated: z.boolean(),
  createdAt: z.string(),
  rawLog: z.unknown().nullable(),
});

const apiIncidentDetailResponseSchema = z.object({
  incident: apiIncidentSummarySchema,
  timeline: z.array(apiTimelineEntrySchema),
  connectors: z.array(apiConnectorSchema),
  diagnostics: apiDiagnosticsSchema,
  concernedLogs: z.array(apiConcernedLogSchema),
});

export type IncidentPaginationMeta = z.infer<typeof paginationMetaSchema>;
export type IncidentDashboardTimeRange = z.infer<
  typeof incidentDashboardTimeRangeSchema
>;
export type IncidentDashboardStats = z.infer<
  typeof incidentDashboardStatsSchema
>;
export type IncidentOmpSummary = z.infer<typeof incidentOmpSummarySchema>;
export type IncidentOmpProposal = z.infer<typeof ompProposalSchema>;
export type IncidentOmpAssetContext = z.infer<typeof ompAssetContextSchema>;

export type IncidentListItem = Omit<
  z.infer<typeof apiIncidentSummarySchema>,
  'omp'
> & {
  omp?: IncidentOmpSummary | null;
};

export type TriggeredIncidentListItem = z.infer<
  typeof apiTriggeredIncidentItemSchema
>;

export type IncidentPrimaryAsset = {
  id: string;
  name: string;
  serialNumber: string;
};

type ApiIncidentSummary = z.infer<typeof apiIncidentSummarySchema>;
type ApiIncidentListResponse = z.infer<typeof apiIncidentListResponseSchema>;
type ApiIncidentDetailResponse = z.infer<
  typeof apiIncidentDetailResponseSchema
>;
type ApiDiagnoseIncidentResponse = z.infer<
  typeof apiDiagnoseIncidentResponseSchema
>;
type ApiTriggerRemoteActionResponse = z.infer<
  typeof apiTriggerRemoteActionResponseSchema
>;
type ApiLaunchOnsiteInterventionResponse = z.infer<
  typeof apiLaunchOnsiteInterventionResponseSchema
>;
type ApiTransitionIncidentResponse = z.infer<
  typeof apiTransitionIncidentResponseSchema
>;

export type TransitionIncidentStatusParams = {
  incidentId: string;
  nextStatus: IncidentStatus;
  blockingReason?: BlockingReason | null;
};

export type IncidentRemoteActionExecutionResponse =
  ApiTriggerRemoteActionResponse;

export type IncidentOnsiteInterventionResponse =
  ApiLaunchOnsiteInterventionResponse;

export type IncidentListResponse = {
  data: IncidentListItem[];
  meta: IncidentPaginationMeta;
};

export type ListIncidentsParams = {
  page: number;
  limit: number;
  q?: string;
};

function toIncidentSeverity(
  severity: number | null,
  severityLevel: string | null,
): IncidentSeverity {
  const normalizedSeverity =
    severity ??
    (severityLevel
      ? Number.parseInt(severityLevel.replace(/[^0-9]/g, ''), 10)
      : Number.NaN);

  if (normalizedSeverity <= 1) {
    return 'critical';
  }

  if (normalizedSeverity === 2) {
    return 'high';
  }

  if (normalizedSeverity === 3) {
    return 'medium';
  }

  return 'low';
}

function toDashboardIncident(incident: ApiIncidentSummary): DemoIncident {
  return mapIncidentListItemToDashboard(toIncidentListItem(incident));
}

export function mapIncidentListItemToDashboard(
  incident: IncidentListItem,
): DemoIncident {
  const totalChargers = Math.max(
    incident.totalChargers,
    incident.affectedChargers,
  );
  const totalStarts = Math.max(totalChargers, 1) * 20;
  const onlineChargers = Math.max(totalChargers - incident.affectedChargers, 0);
  const successfulStarts = Math.round(
    totalStarts * (onlineChargers / Math.max(totalChargers, 1)),
  );

  return {
    id: incident.id,
    code: incident.code || incident.id,
    identifier: incident.identifier,
    stationId: incident.stationId,
    stationName: incident.stationName,
    location: incident.location,
    severity: toIncidentSeverity(incident.severity, incident.severityLevel),
    status: incident.status,
    availableTransitions: incident.availableTransitions ?? [],
    resolutionType: incident.resolutionType,
    blockingReason: incident.blockingReason,
    title: incident.title,
    aiSummary:
      incident.aiSummary ?? incident.description ?? 'No AI summary available.',
    origin: incident.origin.length > 0 ? incident.origin : ['AI diagnosis'],
    createdAt: incident.createdAt,
    updatedAt: incident.updatedAt,
    openedAt: incident.openedAt,
    affectedChargers: incident.affectedChargers,
    totalChargers,
    successfulStarts,
    totalStarts,
    primaryAsset: incident.primaryAsset ?? null,
  };
}

function toIncidentListItem(incident: ApiIncidentSummary): IncidentListItem {
  return {
    id: incident.id,
    identifier: incident.identifier,
    code: incident.code,
    title: incident.title,
    status: incident.status,
    availableTransitions: incident.availableTransitions ?? [],
    resolutionType: incident.resolutionType ?? null,
    blockingReason: incident.blockingReason ?? null,
    severity: incident.severity,
    severityLevel: incident.severityLevel,
    priority: incident.priority,
    description: incident.description,
    aiSummary: incident.aiSummary,
    origin: incident.origin.length > 0 ? incident.origin : ['AI diagnosis'],
    stationId: incident.stationId,
    stationName: incident.stationName,
    location: incident.location,
    affectedChargers: incident.affectedChargers,
    totalChargers: incident.totalChargers,
    createdAt: incident.createdAt,
    updatedAt: incident.updatedAt,
    openedAt: incident.openedAt,
    resolvedAt: incident.resolvedAt,
    closedAt: incident.closedAt,
    primaryAsset: incident.primaryAsset ?? null,
    omp: incident.omp ?? null,
  };
}

function toTimelineEntry(
  entry: ApiIncidentDetailResponse['timeline'][number],
): TimelineEntry {
  return {
    time: entry.occurredAt,
    source:
      entry.source === 'external'
        ? 'external'
        : entry.source === 'client-complaint'
          ? 'client-complaint'
          : 'machine-log',
    sourceLabel: entry.sourceLabel,
    title: entry.title,
    description: entry.description ?? 'No description available.',
    rawCode: entry.rawCode ?? undefined,
    translatedCode: entry.translatedCode ?? undefined,
    normalizedState: entry.normalizedState ?? undefined,
    stateConflict: entry.stateConflict ?? false,
  };
}

function normalizeTimelineTitle(title: string): string {
  return title
    .replace(/^\[TIER_\d+\]\s*(?:\/|\|)\s*/, '')
    .trim()
    .toLowerCase();
}

function deduplicateTimelineEntries(entries: TimelineEntry[]): TimelineEntry[] {
  const seenEntries = new Set<string>();

  return entries.filter((entry) => {
    const entryKey = `${entry.time}::${normalizeTimelineTitle(entry.title)}`;

    if (seenEntries.has(entryKey)) {
      return false;
    }

    seenEntries.add(entryKey);
    return true;
  });
}

function toConcernedLog(
  log: ApiIncidentDetailResponse['concernedLogs'][number],
): ConcernedLog {
  return {
    logId: log.logId,
    receivedAt: log.receivedAt,
    isTreated: log.isTreated,
    createdAt: log.createdAt,
    rawLog: log.rawLog,
  };
}

function toConnectorSnapshot(
  connector: ApiIncidentDetailResponse['connectors'][number],
): ConnectorSnapshot {
  const normalizedStatus = connector.status?.toLowerCase() ?? 'faulted';
  const status: ConnectorSnapshot['status'] =
    normalizedStatus === 'available'
      ? 'available'
      : normalizedStatus === 'derated'
        ? 'derated'
        : normalizedStatus === 'offline'
          ? 'offline'
          : 'faulted';

  return {
    code: connector.code,
    name: connector.name,
    connectorType: connector.connectorType?.toLowerCase().includes('ac')
      ? 'AC Type 2'
      : 'CCS2',
    status,
    health:
      status === 'available' ? 'good' : status === 'derated' ? 'fair' : 'poor',
    powerKw: 0,
    note: connector.note ?? 'No connector note available.',
  };
}

function toDiagnostics(
  diagnostics: ApiIncidentDetailResponse['diagnostics'],
  incident: ApiIncidentSummary,
): AIDiagnostics {
  const recommendationSnapshot = diagnosisSnapshotSchema.safeParse(
    diagnostics.latestDiagnosisSnapshot ?? null,
  );
  const armExecutionCommands = recommendationSnapshot.success
    ? (recommendationSnapshot.data.armRemediationCommands?.commands ?? []).map(
        (command) => ({
          commandId: command.commandId,
          command: command.command,
          actionId: command.actionId,
          createdAt: command.createdAt,
          operation: command.operation,
          protocol: command.protocol,
          remediationPlanId: command.remediationPlanId,
          status: (command.isSuccess === true
            ? 'SUCCESS'
            : command.isSuccess === false
              ? 'FAILED'
              : 'PENDING') as 'SUCCESS' | 'FAILED' | 'PENDING',
          targetAssetId: command.targetAssetId,
        }),
      )
    : [];
  const recommendedRemoteActions = recommendationSnapshot.success
    ? (recommendationSnapshot.data.diagnosis?.remediation?.actions ?? [])
        .filter((action) => action.remote)
        .map((action, index) => ({
          id: action.actionDisplayId?.trim() || `remote-action-${index + 1}`,
          description:
            action.actionDescription?.trim() || 'Remote action available',
          command: action.command?.trim(),
        }))
    : [];

  return {
    incidentSummary:
      diagnostics.incidentSummary ??
      incident.aiSummary ??
      incident.description ??
      'No incident summary available.',
    rootCauseAnalysis:
      diagnostics.rootCauseAnalysis ?? 'No root cause analysis available.',
    armExecutionCommands,
    recommendedRemoteActions,
    remoteActions: diagnostics.remoteActions.map((action) => ({
      action: action.action,
      status:
        action.status.toUpperCase() === 'SUCCESS'
          ? 'SUCCESS'
          : action.status.toUpperCase() === 'PENDING'
            ? 'PENDING'
            : 'FAILED',
      timestamp: action.timestamp,
    })),
    recommendedOnSiteActions: diagnostics.recommendedOnSiteActions,
    estimatedResolutionTime:
      diagnostics.estimatedResolutionTime ?? 'Not available',
    partsRequired: diagnostics.partsRequired.map((part) => ({
      partNumber: part.partNumber ?? 'N/A',
      description: part.description,
    })),
    latestDiagnosisSnapshot: diagnostics.latestDiagnosisSnapshot ?? null,
    autoDiagnosisDueAt: diagnostics.autoDiagnosisDueAt ?? null,
    rulesUsed: diagnostics.rulesUsed ?? [],
    rulesUnavailable: diagnostics.rulesUnavailable ?? false,
  };
}

export const IncidentService = {
  async getDashboardStats(params: {
    timeRange?: IncidentDashboardTimeRange;
    kpiTimeRange?: IncidentDashboardTimeRange;
    chartRange?: '1m' | '1y' | 'all';
  }): Promise<IncidentDashboardStats> {
    const searchParams = new URLSearchParams();
    if (params.timeRange) {
      searchParams.set('timeRange', params.timeRange);
    }
    if (params.kpiTimeRange) {
      searchParams.set('kpiTimeRange', params.kpiTimeRange);
    }
    if (params.chartRange) {
      searchParams.set('chartRange', params.chartRange);
    }

    const query = searchParams.toString();
    const response = await fetch(
      buildApiUrl(
        `/api/v1/incidents/dashboard-stats${query ? `?${query}` : ''}`,
      ),
      {
        method: 'GET',
        credentials: 'include',
      },
    );

    if (!response.ok) {
      const errorPayload = await response
        .json()
        .catch(() => ({ message: response.statusText }));

      throw new Error(
        errorPayload.message || 'Failed to fetch incident dashboard stats',
      );
    }

    return incidentDashboardStatsSchema.parse(await response.json());
  },

  async list(params: ListIncidentsParams): Promise<IncidentListResponse> {
    const response = await fetch(buildApiUrl(buildListIncidentsPath(params)), {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      const errorPayload = await response
        .json()
        .catch(() => ({ message: response.statusText }));

      throw new Error(errorPayload.message || 'Failed to fetch incidents');
    }

    const payload = apiIncidentListResponseSchema.parse(
      (await response.json()) as ApiIncidentListResponse,
    );

    return {
      data: payload.data.map((incident) => toIncidentListItem(incident)),
      meta: payload.meta,
    };
  },

  async listTriggered(params: {
    page: number;
    q?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<{
    data: TriggeredIncidentListItem[];
    meta: IncidentPaginationMeta;
  }> {
    const searchParams = new URLSearchParams({
      page: String(params.page),
    });

    if (params.q?.trim()) {
      searchParams.set('q', params.q.trim());
    }

    if (params.sortOrder) {
      searchParams.set('sortOrder', params.sortOrder);
    }

    const response = await fetch(
      buildApiUrl(`/api/v1/incidents/triggered?${searchParams.toString()}`),
      {
        method: 'GET',
        credentials: 'include',
      },
    );

    if (!response.ok) {
      const errorPayload = await response
        .json()
        .catch(() => ({ message: response.statusText }));

      throw new Error(
        errorPayload.message || 'Failed to fetch triggered incidents',
      );
    }

    return apiTriggeredIncidentListResponseSchema.parse(await response.json());
  },

  async findDetailById(incidentId: string): Promise<IncidentDetail> {
    const normalizedIncidentId = incidentId.trim();

    if (!normalizedIncidentId) {
      throw new Error('Incident id is required');
    }

    const response = await fetch(
      buildApiUrl(`/api/v1/incidents/${normalizedIncidentId}`),
      {
        method: 'GET',
        credentials: 'include',
      },
    );

    if (!response.ok) {
      const errorPayload = await response
        .json()
        .catch(() => ({ message: response.statusText }));

      throw new Error(
        errorPayload.message || 'Failed to fetch incident details',
      );
    }

    const payload = apiIncidentDetailResponseSchema.parse(
      (await response.json()) as ApiIncidentDetailResponse,
    );

    return {
      incident: toDashboardIncident(payload.incident),
      timeline: deduplicateTimelineEntries(
        payload.timeline.map((entry) => toTimelineEntry(entry)),
      ),
      diagnostics: toDiagnostics(payload.diagnostics, payload.incident),
      connectors: payload.connectors.map((connector) =>
        toConnectorSnapshot(connector),
      ),
      concernedLogs: payload.concernedLogs.map((log) => toConcernedLog(log)),
      externalApiChecks: [],
    };
  },

  async diagnoseIncident(
    incidentId: string,
  ): Promise<ApiDiagnoseIncidentResponse> {
    const normalizedIncidentId = incidentId.trim();

    if (!normalizedIncidentId) {
      throw new Error('Incident id is required');
    }

    const response = await fetch(
      buildApiUrl(`/api/v1/incidents/${normalizedIncidentId}/diagnose`),
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      },
    );

    if (!response.ok) {
      const errorPayload = await response
        .json()
        .catch(() => ({ message: response.statusText }));

      throw new Error(errorPayload.message || 'Failed to diagnose incident');
    }

    return apiDiagnoseIncidentResponseSchema.parse(
      (await response.json()) as ApiDiagnoseIncidentResponse,
    );
  },

  async triggerRemoteAction(
    incidentId: string,
  ): Promise<IncidentRemoteActionExecutionResponse> {
    const normalizedIncidentId = incidentId.trim();

    if (!normalizedIncidentId) {
      throw new Error('Incident id is required');
    }

    const response = await fetch(
      buildApiUrl(`/api/v1/incidents/${normalizedIncidentId}/remote-actions`),
      {
        method: 'POST',
        credentials: 'include',
      },
    );

    if (!response.ok) {
      const errorPayload = await response
        .json()
        .catch(() => ({ message: response.statusText }));

      throw new Error(
        errorPayload.message || 'Failed to launch remote incident actions',
      );
    }

    return apiTriggerRemoteActionResponseSchema.parse(
      (await response.json()) as ApiTriggerRemoteActionResponse,
    );
  },

  async launchOnsiteIntervention(
    incidentId: string,
  ): Promise<IncidentOnsiteInterventionResponse> {
    const normalizedIncidentId = incidentId.trim();

    if (!normalizedIncidentId) {
      throw new Error('Incident id is required');
    }

    const response = await fetch(
      buildApiUrl(
        `/api/v1/incidents/${normalizedIncidentId}/onsite-interventions`,
      ),
      {
        method: 'POST',
        credentials: 'include',
      },
    );

    if (!response.ok) {
      const errorPayload = await response
        .json()
        .catch(() => ({ message: response.statusText }));

      throw new Error(
        errorPayload.message || 'Failed to launch onsite intervention',
      );
    }

    return apiLaunchOnsiteInterventionResponseSchema.parse(
      (await response.json()) as ApiLaunchOnsiteInterventionResponse,
    );
  },

  async transitionIncidentStatus(
    params: TransitionIncidentStatusParams,
  ): Promise<ApiTransitionIncidentResponse> {
    const normalizedIncidentId = params.incidentId.trim();

    if (!normalizedIncidentId) {
      throw new Error('Incident id is required');
    }

    const response = await fetch(
      buildApiUrl(
        `/api/v1/incidents/${normalizedIncidentId}/status-transitions`,
      ),
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nextStatus: params.nextStatus,
          ...(params.blockingReason
            ? { blockingReason: params.blockingReason }
            : {}),
        }),
      },
    );

    if (!response.ok) {
      const errorPayload = await response
        .json()
        .catch(() => ({ message: response.statusText }));

      throw new Error(
        errorPayload.message || 'Failed to transition incident status',
      );
    }

    return apiTransitionIncidentResponseSchema.parse(
      (await response.json()) as ApiTransitionIncidentResponse,
    );
  },
};
